import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { Icon } from '@cotera/client/app/components';
import { TimeInterval, ATC_RETRY_MAX_BATCH } from '@cotera/api';
import { atcQueryKeys } from './query-keys';
import { useOrgNamesLazy } from './hooks/use-org-names';
import { CopyButton } from './components/copy-button';
import { RetryConfirmationDialog } from './components/retry-confirmation-dialog';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

export interface FailureTypeRow {
  failureType: string;
  count: number;
  exampleOrgId: string;
  exampleRunId: string;
  exampleId: string;
  sagaMetadata?: { sagaType: string | null; entityName: string | null };
  // Per-org counts for this failure type (counts only, no invocation link).
  orgBreakdown?: { orgId: string; count: number }[];
}

// When provided, each org row in the breakdown gets a "Retry as new" button
// that restarts that org's failed AtcWorkflow runs for this error. Only wired
// for the ATC Workflow tab.
export interface FailureRetryConfig {
  timeInterval: TimeInterval;
}

interface FailureTypesTableProps {
  data: FailureTypeRow[];
  retryConfig?: FailureRetryConfig;
}

export const FailureTypesTable: React.FC<FailureTypesTableProps> = ({
  data,
  retryConfig,
}) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (failureType: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [failureType]: !prev[failureType],
    }));
  };

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-700">Failure Types</h3>
      <div className="bg-white rounded-lg shadow overflow-auto max-h-[600px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>Failure Type</TableHead>
              <TableHead>Count</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row) => (
              <React.Fragment key={row.failureType}>
                <TableRow>
                  <TableCell>
                    <button
                      onClick={() => toggleRow(row.failureType)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {expandedRows[row.failureType] ? (
                        <Icon icon="chevron-down" className="w-4 h-4" />
                      ) : (
                        <Icon icon="chevron-right" className="w-4 h-4" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs">{row.failureType}</span>
                  </TableCell>
                  <TableCell>{row.count}</TableCell>
                </TableRow>
                {expandedRows[row.failureType] && (
                  <TableRow>
                    <TableCell colSpan={3} className="bg-gray-50">
                      <div className="space-y-4">
                        {row.orgBreakdown !== undefined &&
                          row.orgBreakdown.length > 0 && (
                            <OrgBreakdownTable
                              breakdown={row.orgBreakdown}
                              failureType={row.failureType}
                              retryConfig={retryConfig}
                            />
                          )}
                        <FailureTypeDetails
                          orgId={row.exampleOrgId}
                          runId={row.exampleRunId}
                          invocationId={row.exampleId}
                          sagaMetadata={row.sagaMetadata}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const OrgBreakdownTable: React.FC<{
  breakdown: { orgId: string; count: number }[];
  failureType?: string;
  retryConfig?: FailureRetryConfig;
}> = ({ breakdown, failureType, retryConfig }) => {
  const client = useUntenantedClient();
  const queryClient = useQueryClient();

  const orgIds = useMemo(
    () => [...new Set(breakdown.map((b) => b.orgId))],
    [breakdown]
  );
  const orgMap = useOrgNamesLazy(orgIds, orgIds.length > 0);

  const sorted = useMemo(
    () => [...breakdown].sort((a, b) => b.count - a.count),
    [breakdown]
  );

  const canRetry = retryConfig !== undefined && failureType !== undefined;

  const [confirmTarget, setConfirmTarget] = useState<{
    orgId: string;
    orgName: string;
    count: number;
  } | null>(null);
  const [resultMessage, setResultMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const retryMutation = useMutation({
    mutationFn: async (orgId: string) => {
      if (retryConfig === undefined || failureType === undefined) {
        throw new Error('Retry is not available here');
      }
      const res = await client.atcVisibility.restartAtcFailuresAsNew({
        timeInterval: retryConfig.timeInterval,
        failureType,
        orgId,
      });
      return Assert.assertOk(res);
    },
    onSuccess: (data) => {
      setConfirmTarget(null);
      setResultMessage({
        type: data.failed > 0 ? 'error' : 'success',
        text:
          data.failed > 0
            ? `Restarted ${data.succeeded} as new, ${data.failed} failed${
                data.sampleError !== undefined ? `: ${data.sampleError}` : ''
              }.`
            : `Restarted ${data.succeeded} run(s) as new.`,
      });
      void queryClient.invalidateQueries({
        queryKey: atcQueryKeys.all,
        refetchType: 'all',
      });
    },
    onError: (error: Error) => {
      setConfirmTarget(null);
      setResultMessage({ type: 'error', text: error.message });
    },
  });

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-800">
        Impacted Organizations
      </h4>
      {resultMessage !== null && (
        <div
          className={`p-2 rounded-md text-xs ${
            resultMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {resultMessage.text}
        </div>
      )}
      <div className="bg-white rounded-md border overflow-hidden w-1/2">
        <Table>
          <TableHeader>
            <TableRow>
              {canRetry && <TableHead className="w-32">Actions</TableHead>}
              <TableHead>Organization</TableHead>
              <TableHead className="pl-8">Count</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((b) => (
              <TableRow key={b.orgId}>
                {canRetry && (
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={b.count > ATC_RETRY_MAX_BATCH}
                      title={
                        b.count > ATC_RETRY_MAX_BATCH
                          ? `Too many failed runs to retry at once (limit ${ATC_RETRY_MAX_BATCH}) — narrow the time range`
                          : undefined
                      }
                      onClick={() =>
                        setConfirmTarget({
                          orgId: b.orgId,
                          orgName: orgMap?.get(b.orgId) ?? b.orgId,
                          count: b.count,
                        })
                      }
                    >
                      Retry as new
                    </Button>
                  </TableCell>
                )}
                <TableCell>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-gray-700">
                      {orgMap?.get(b.orgId) ?? b.orgId}
                    </span>
                    <CopyButton
                      value={b.orgId}
                      title={`Copy org ID: ${b.orgId}`}
                    />
                  </span>
                </TableCell>
                <TableCell className="pl-8">{b.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <RetryConfirmationDialog
        open={confirmTarget !== null}
        orgName={confirmTarget?.orgName ?? ''}
        count={confirmTarget?.count ?? 0}
        failureType={failureType ?? ''}
        isPending={retryMutation.isPending}
        onConfirm={() => {
          if (confirmTarget !== null) {
            retryMutation.mutate(confirmTarget.orgId);
          }
        }}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
};

const FailureTypeDetails: React.FC<{
  orgId: string;
  runId: string;
  invocationId: string;
  sagaMetadata?: { sagaType: string | null; entityName: string | null };
}> = ({ orgId, runId, invocationId, sagaMetadata }) => {
  const isSaga = orgId.startsWith('saga-');

  if (isSaga) {
    return (
      <SagaFailureDetails
        orgKey={orgId}
        invocationId={invocationId}
        sagaType={sagaMetadata?.sagaType ?? null}
        entityName={sagaMetadata?.entityName ?? null}
      />
    );
  }

  return (
    <AtcFailureDetails
      orgId={orgId}
      runId={runId}
      invocationId={invocationId}
    />
  );
};

const SagaFailureDetails: React.FC<{
  orgKey: string;
  invocationId: string;
  sagaType: string | null;
  entityName: string | null;
}> = ({ orgKey, invocationId, sagaType, entityName }) => {
  const client = useUntenantedClient();

  const { data: restateUrlData } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getRestateUrl({});
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.restateUrl(),
  });

  // Key format: saga-{scope}-{sagaId} where sagaId is a UUID (36 chars)
  const withoutPrefix = orgKey.slice(5); // remove "saga-"
  const sagaId = withoutPrefix.slice(-36);
  const scope = withoutPrefix.slice(0, -37); // remove "-{sagaId}"

  const { data: orgs } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getOrgsByIds({ orgIds: [scope] });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.organizationsByIds([scope]),
    enabled: scope !== 'system',
  });

  const orgName =
    scope === 'system'
      ? 'System'
      : orgs?.find((o) => o.id === scope)?.name ?? scope;

  const invocationUrl =
    restateUrlData !== undefined
      ? `${restateUrlData.restateUrl}/ui/invocations/${invocationId}`
      : null;

  const fields = [
    { label: 'Saga Type', value: sagaType ?? 'Unknown', mono: true },
    ...(entityName !== null ? [{ label: 'Entity', value: entityName }] : []),
    { label: 'Org', value: orgName },
    { label: 'Org ID', value: scope, mono: true },
    { label: 'Saga ID', value: sagaId, mono: true },
  ];

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-800">
        Saga Workflow Failure
      </h4>
      <Table>
        <TableBody>
          {invocationUrl !== null && (
            <TableRow>
              <TableCell className="font-medium text-gray-700 w-32">
                Invocation
              </TableCell>
              <TableCell>
                <a
                  href={invocationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {invocationId}
                </a>
              </TableCell>
            </TableRow>
          )}
          {fields.map((field) => (
            <TableRow key={field.label}>
              <TableCell className="font-medium text-gray-700 w-32">
                {field.label}
              </TableCell>
              <TableCell
                className={
                  field.mono === true
                    ? 'font-mono text-xs text-gray-600'
                    : 'text-gray-600'
                }
              >
                {field.value}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const AtcFailureDetails: React.FC<{
  orgId: string;
  runId: string;
  invocationId: string;
}> = ({ orgId, runId, invocationId }) => {
  const client = useUntenantedClient();

  const { data, isLoading, error } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getFailureTypeDetails({
        orgId,
        runId,
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.failureTypeDetails(orgId, runId),
  });

  const { data: restateUrlData } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getRestateUrl({});
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.restateUrl(),
  });

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading details...</div>;
  }

  if (error !== null) {
    return <div className="text-sm text-red-600">Error loading details</div>;
  }

  if (data === null || data === undefined) {
    return <div className="text-sm text-gray-500">No details available</div>;
  }

  const invocationUrl =
    restateUrlData !== undefined
      ? `${restateUrlData.restateUrl}/ui/invocations/${invocationId}`
      : null;

  const fields = [
    { label: 'Run ID', value: runId, mono: true },
    { label: 'Org ID', value: data.orgId, mono: true },
    { label: 'Organization', value: data.organizationName ?? 'Unknown' },
    { label: 'Agent ID', value: data.agentId ?? 'N/A', mono: true },
    { label: 'Model', value: data.model ?? 'N/A' },
    { label: 'Tools', value: data.tools ?? 'N/A' },
  ];

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-800">Example Run</h4>
      <Table>
        <TableBody>
          {invocationUrl !== null && (
            <TableRow>
              <TableCell className="font-medium text-gray-700 w-32">
                Invocation ID
              </TableCell>
              <TableCell>
                <a
                  href={invocationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {invocationId}
                </a>
              </TableCell>
            </TableRow>
          )}
          {fields.map((field) => (
            <TableRow key={field.label}>
              <TableCell className="font-medium text-gray-700 w-32">
                {field.label}
              </TableCell>
              <TableCell
                className={
                  field.mono === true
                    ? 'font-mono text-xs text-gray-600'
                    : 'text-gray-600'
                }
              >
                {field.value}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
